import type { User } from "oidc-client-ts";
import { PropsWithChildren, useEffect, useRef, useState } from "react";
import { hasAuthParams, useAuth } from "react-oidc-context";
import { useLocation } from "react-router";
import {
  isTokenExpired,
  redirectToLogin,
  redirectToLoginWhenSessionEnds,
  renewSession,
} from "./session.ts";

export const AutomaticLogin = ({ children }: PropsWithChildren) => {
  const auth = useAuth();
  const hasTriedSignIn = useRef(false);
  const previousUserRef = useRef<User | null | undefined>(undefined);
  const location = useLocation();
  const [sessionHasStarted, setSessionHasStarted] = useState(false);

  const hasUsableIdToken = !!auth.user?.id_token && !isTokenExpired(auth.user);
  if (hasUsableIdToken && !sessionHasStarted) {
    setSessionHasStarted(true);
  }

  // The session flow, as a decision tree
  //
  //   id token usable?
  //   ├── no  ─── renewal failed?        → back to the login page now
  //   │       └── a renewal produced it? → stop the background renewal and
  //   │                                    go back once the session ended
  //   │       └── otherwise               → attempt to recover the session
  //   └── yes ─── access token usable?
  //           └── yes                    → nothing to do
  //           └── no                      → attempt to recover the session
  useEffect(() => {
    const previousUser = previousUserRef.current;
    previousUserRef.current = auth.user;

    const storeReturnLocation = () => {
      if (!hasAuthParams()) {
        sessionStorage.setItem("original-location-pathname", location.pathname);
        sessionStorage.setItem("original-location-hash", location.hash);
        sessionStorage.setItem("original-location-search", location.search);
      }
    };

    const backgroundRenewalFailed = !!auth.error && !hasUsableIdToken;
    if (backgroundRenewalFailed) {
      storeReturnLocation();
      void redirectToLogin(auth);
      return;
    }

    if (auth.user) {
      const renewedIntoDyingSession =
        !!previousUser &&
        auth.user.id_token !== previousUser.id_token &&
        !hasUsableIdToken;
      if (renewedIntoDyingSession) {
        storeReturnLocation();
        auth.stopSilentRenew();
        redirectToLoginWhenSessionEnds(auth, auth.user.profile.exp ?? 0);
        return;
      }
    }

    const hasUsableAccessToken = auth.user?.expired === false;
    const sessionIsFullyUsable = hasUsableIdToken && hasUsableAccessToken;
    const signinFlowIsInFlight =
      hasAuthParams() || auth.activeNavigator || auth.isLoading;
    if (
      sessionIsFullyUsable ||
      signinFlowIsInFlight ||
      hasTriedSignIn.current
    ) {
      return;
    }

    hasTriedSignIn.current = true;
    storeReturnLocation();
    const attemptLogin = async () => {
      let renewedUser: User | null = null;
      if (auth.user) {
        try {
          renewedUser = await renewSession(auth);
        } catch {
          renewedUser = null;
        }
      }
      if (!renewedUser && !hasUsableIdToken) {
        await redirectToLogin(auth);
      }
    };
    void attemptLogin();
  }, [auth, location, hasUsableIdToken]);

  return sessionHasStarted ? children : null;
};
